import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getUserCollaborationById, updateUserCollaboration } from "../../api/colaboracoes";
import { listImagesByCollaboration } from "../../api/admin/images";
import { listCommentsByCollaboration } from "../../api/admin/comments";
import { toFrontStatus } from "../../api/admin/status";
import CollaborationDetails, { detailGroups } from "../../pages/TelaAdministrador/colaborar/CollaborationDetails";

export default function MyRequestDetails() {
  const { id } = useParams();
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const { user, getCsrfToken } = useAuth();
  const token = getCsrfToken();
  const role = user?.tipo === "Colaborador" ? "Colaborador" : user?.tipo === "Admin" ? "Admin" : "Comum";
  const suffix = role === "Comum" ? "usuario-comum" : "colaborador";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [status, setStatus] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [images, setImages] = useState<{ id: number; url: string; code?: string; status?: string }[]>([]);
  const [comments, setComments] = useState<{ id: number; title?: string; text?: string; author?: string }[]>([]);
  const editing = role === "Colaborador" && status === "correction" && query.get("editar") === "1";
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        if (!id || !/^\d+$/.test(id)) throw new Error("ID inválido");
        const [detail, imagePage, commentPage] = await Promise.all([
          getUserCollaborationById(id, token), listImagesByCollaboration(Number(id), token || "", 1, 100),
          listCommentsByCollaboration(Number(id), token || "", 1, 100),
        ]);
        if (cancelled) return;
        const loaded: Record<string, string> = {};
        const raw = detail as unknown as Record<string, unknown>;
        detailGroups.flatMap(group => group.fields).forEach(field => {
          const value = raw[field.key];
          loaded[field.name] = value == null ? "" : String(value);
        });
        loaded.collectionDate = loaded.collectionDate.split("T")[0];
        loaded.observations = detail.observacoes || "";
        setValues(loaded); setSavedValues(loaded); setStatus(detail.status);
        setImages(imagePage.items.map((image: any) => ({ id: image.imagem_id, url: image.url_imagem, code: image.codigo_imagem, status: toFrontStatus(image.status) })));
        setComments(commentPage.items.map((comment: any) => ({ id: comment.comentario_id || comment.id, title: comment.assunto, text: comment.conteudo, author: comment.autor?.nome_completo || comment.user?.nome_completo })));
      } catch { if (!cancelled) setError("Não foi possível carregar a solicitação. Volte à lista e tente novamente."); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, token]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing || saving || !id) return;
    setSaving(true); setError(""); setSuccess("");
    try {
      const payload: Record<string, string> = { observacoes: values.observations };
      detailGroups.flatMap(group => group.fields).forEach(field => { payload[field.key] = values[field.name]; });
      await updateUserCollaboration(id, payload, token || "");
      setSavedValues({ ...values }); setSuccess("Solicitação atualizada com sucesso.");
    } catch { setError("Não foi possível salvar. Verifique os campos e tente novamente."); }
    finally { setSaving(false); }
  };
  return <CollaborationDetails role={role} id={id} userName={user?.nome_completo || ""} loading={loading} saving={saving}
    error={loading || !status ? error : undefined} status={status} editing={editing} values={values} images={images} comments={comments}
    feedback={<>{success && <div role="status" className="alert alert-success">{success}</div>}{status && error && <div role="alert" className="alert alert-danger">{error}</div>}</>}
    onBack={() => navigate(`/minhas-colaboracoes-${suffix}`)} onSubmit={save}
    onChange={event => setValues(previous => ({ ...previous, [event.target.name]: event.target.value }))}
    onCancel={() => { setValues(savedValues); setError(""); setSuccess(""); }} />;
}
